import { SyntheticReportColumn } from '@liquitics/transaction';
import sqlFormatter from 'sql-formatter';

export interface SyntheticColumnSnippetContext {
    customerColumnAliases: { [key: string]: string };
    syntheticReportColumns: SyntheticReportColumn[];
    distinctValues: { [columnName: string]: string[] };
}

export function syntheticReportColumnToSnippet(src: SyntheticReportColumn): string[] {
    let formattedCase = sqlFormatter.format(src.caseStatement, {
        language: 'sql',
        indent: '\t'
    });
    formattedCase = formattedCase.split('\n').join('\n\t');
    return [`snippet $custom:${src.columnName}`, `\t${formattedCase}\n`];
}

function literalsToSnippet(type: string, literals: string[]): string[] {
    const snippets = [];
    literals
        .sort((l1, l2) => {
            return l1 < l2 ? -1 : 1;
        })
        .forEach((literal) => {
            snippets.push(`snippet $${type}:${literal}`);
            snippets.push(`\t'${literal}'`);
        })
    return snippets;
}

export function createSnippetTextGenerator(context: SyntheticColumnSnippetContext): { [key: string]: (context: SyntheticColumnSnippetContext) => string } {
    const generator = {
        BASE: (context: SyntheticColumnSnippetContext) => {
            const snippetTextArray: string[] = [];
            snippetTextArray.push(
                'snippet casewhen',
                '\tCASE\n\
\t\tWHEN ${1:condition1} THEN ${2:result1}\n\
\t\tWHEN ${3:condition2} THEN ${4:result2}\n\
\t\tELSE ${5:result}\n\
\tEND',
                ''
            );
            snippetTextArray.push(
                'snippet whenthen',
                '\tWHEN ${1:condition} THEN ${2:result}',
                ''
            );
            const sortedColumns = Object.keys(context.customerColumnAliases)
                .sort((c1, c2) => {
                    const a1 = context.customerColumnAliases[c1];
                    const a2 = context.customerColumnAliases[c2];
                    return a1 < a2 ? -1 : 1;
                });
            sortedColumns
                .forEach((column) => {
                    const alias = context.customerColumnAliases[column];
                    snippetTextArray.push(`snippet ${alias}`);
                    snippetTextArray.push(`\t${column}`);
                });

            return snippetTextArray.join('\n');
        },
        ['$custom']: (context: SyntheticColumnSnippetContext) => {
            const snippetTextArray: string[] = [];
            const sortedSRCs = context.syntheticReportColumns
                .sort((src1, src2) => {
                    return src1.columnName < src2.columnName ? -1 : 1;
                });
            sortedSRCs
                .forEach((src) => {
                    snippetTextArray.push(...syntheticReportColumnToSnippet(src));
                });
            return snippetTextArray.join('\n');
        },
    };
    const distinctColumnNames = Object.keys(context.distinctValues);
    generator['$literal'] = (context: SyntheticColumnSnippetContext) => {
        const snippetTextArray: string[] = [];
        distinctColumnNames
            .sort((c1, c2) => {
                return c1 < c2 ? -1 : 1;
            })
            .forEach((columnName) => {
                const alias = context.customerColumnAliases[columnName];
                let aliasedColumnName;
                if (alias) {
                    aliasedColumnName = `${columnName}(${alias})`;
                } else {
                    aliasedColumnName = columnName;
                }
                snippetTextArray.push(`snippet $literal-${aliasedColumnName}`);
                snippetTextArray.push(`\t\\$${columnName}`);
            })
        return snippetTextArray.join('\n');
    };

    distinctColumnNames
        .forEach((columnName) => {
            const distinctValues = context.distinctValues[columnName];
            generator[`$${columnName}`] = (context: SyntheticColumnSnippetContext) => {
                const snippetTextArray: string[] = [];
                snippetTextArray.push(...literalsToSnippet(columnName, distinctValues));
                return snippetTextArray.join('\n');
            };
        });
    return generator;
}

